<div class="content-body">
  <app-breadcrumb [breadcrumb]="breadcrumbList"></app-breadcrumb>

  <div class="container-fluid">
  <div class="card">
    <div class="card-body">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="heading mb-0">Accounts Reports</h5>
        <div class="d-flex gap-2 align-items-center" *ngIf="checkDownloadPermission">
          <!-- Download buttons -->
          <button
            class="btn btn-outline-danger btn-sm"
            (click)="downloadReport('pdf')"
            [disabled]="isDownloading">
            <span *ngIf="isDownloading && activeDownloadType === 'pdf'" class="spinner-border spinner-border-sm me-1"></span>
            <i class="bi bi-file-earmark-pdf me-1" *ngIf="!isDownloading || activeDownloadType !== 'pdf'"></i>
            PDF
          </button>
          <button
            class="btn btn-outline-success btn-sm"
            (click)="downloadReport('csv')"
            [disabled]="isDownloading">
            <span *ngIf="isDownloading && activeDownloadType === 'csv'" class="spinner-border spinner-border-sm me-1"></span>
            <i class="bi bi-file-earmark-spreadsheet me-1" *ngIf="!isDownloading || activeDownloadType !== 'csv'"></i>
            CSV
          </button>
          <button
            class="btn btn-outline-primary btn-sm"
            (click)="openShareModal('email')"
            [disabled]="isDownloading">
            <i class="bi bi-envelope me-1"></i> Email
          </button>
          <button
            class="btn btn-outline-success btn-sm"
            (click)="openShareModal('whatsapp')"
            [disabled]="isDownloading">
            <i class="bi bi-whatsapp me-1"></i> WhatsApp
          </button>
          <span *ngIf="isDownloading" class="text-muted small ms-2">{{ downloadProgress }}</span>
        </div>
      </div>

      <!-- Filters -->
      <form [formGroup]="filterForm">
        <div class="row g-2 mb-3">
          <div class="col-md-2 col-sm-6">
            <label class="form-label" title="Filters bills by the date they were paid; unpaid bills are excluded when a range is set">Paid Start Date</label>
            <input type="date" class="form-control form-control-sm" formControlName="start_date" (change)="onFilterChange()">
          </div>
          <div class="col-md-2 col-sm-6">
            <label class="form-label" title="Filters bills by the date they were paid; unpaid bills are excluded when a range is set">Paid End Date</label>
            <input type="date" class="form-control form-control-sm" formControlName="end_date"
              [min]="filterForm.get('start_date')?.value" (change)="onFilterChange()">
          </div>
          <div class="col-md-2 col-sm-6">
            <label class="form-label">Property</label>
            <ng-select
              formControlName="property_id"
              [items]="propertiesItems"
              bindLabel="label"
              bindValue="value"
              placeholder="All Properties"
              [virtualScroll]="true"
              (scrollToEnd)="onPropertyScrollEnd()"
              (search)="searchProperty$.next($event.term)"
              (change)="onFilterChange()"
              (clear)="onFilterChange()"
              class="select2-sm">
            </ng-select>
          </div>
          <div class="col-md-2 col-sm-6">
            <label class="form-label">Manager</label>
            <ng-select
              formControlName="managed_by"
              [items]="managersItems"
              bindLabel="label"
              bindValue="value"
              placeholder="All Managers"
              [multiple]="true"
              [closeOnSelect]="false"
              [virtualScroll]="true"
              (scrollToEnd)="onManagerScrollEnd()"
              (search)="searchManager$.next($event.term)"
              (change)="onFilterChange()"
              (clear)="onFilterChange()"
              class="select2-sm">
            </ng-select>
          </div>
          <div class="col-md-2 col-sm-6">
            <label class="form-label">Bill Type</label>
            <ng-select
              formControlName="bill_type"
              [items]="billTypeOptions"
              bindLabel="label"
              bindValue="value"
              placeholder="All Types"
              [multiple]="true"
              (change)="onFilterChange()"
              (clear)="onFilterChange()"
              class="select2-sm">
            </ng-select>
          </div>
          <div class="col-md-1 col-sm-6">
            <label class="form-label">Status</label>
            <ng-select
              formControlName="status"
              [items]="statusOptions"
              bindLabel="label"
              bindValue="value"
              placeholder="All"
              (change)="onFilterChange()"
              (clear)="onFilterChange()"
              class="select2-sm">
            </ng-select>
          </div>
          <div class="col-md-1 col-sm-6">
            <label class="form-label">Invoice</label>
            <ng-select
              formControlName="invoice_generated"
              [items]="invoiceOptions"
              bindLabel="label"
              bindValue="value"
              placeholder="All"
              (change)="onFilterChange()"
              (clear)="onFilterChange()"
              class="select2-sm">
            </ng-select>
          </div>
          <div class="col-md-2 col-sm-6">
            <label class="form-label">Bank Account</label>
            <ng-select
              formControlName="bank_account_id"
              [items]="bankAccounts"
              bindLabel="display_name"
              bindValue="id"
              placeholder="All"
              (change)="onFilterChange()"
              (clear)="onFilterChange()"
              class="select2-sm">
            </ng-select>
          </div>
          <div class="col-md-1 col-sm-6 d-flex align-items-end">
            <button class="btn btn-outline-secondary btn-sm w-100" (click)="resetFilters()" [disabled]="!disableClear">
              Clear
            </button>
          </div>
        </div>
      </form>

      <!-- Summary Cards -->
      <div class="d-flex flex-wrap gap-2 mb-3" *ngIf="summaryData">
        <div class="summary-card bg-primary text-white">
          <div class="summary-value">{{ summaryData.total_bills }}</div>
          <div class="summary-label">Total Bills</div>
        </div>
        <div class="summary-card bg-success text-white">
          <div class="summary-value">{{ formatAmount(summaryData.total_paid_amount) }}</div>
          <div class="summary-label">Paid Amount</div>
        </div>
        <div class="summary-card bg-warning text-dark">
          <div class="summary-value">{{ formatAmount(summaryData.total_pending_amount) }}</div>
          <div class="summary-label">Pending Amount</div>
        </div>
        <div class="summary-card bg-info text-dark">
          <div class="summary-value">{{ formatAmount(summaryData.total_amount) }}</div>
          <div class="summary-label">Total Amount</div>
        </div>
        <ng-container *ngIf="summaryData.invoice_count !== undefined">
          <div class="summary-card bg-secondary text-white">
            <div class="summary-value">{{ summaryData.invoice_count }}</div>
            <div class="summary-label">Invoiced</div>
          </div>
          <div class="summary-card bg-light text-dark" style="border: 1px solid #dee2e6;">
            <div class="summary-value">{{ summaryData.non_invoice_count }}</div>
            <div class="summary-label">Non-Invoice</div>
          </div>
        </ng-container>
      </div>

      <!-- Tabs -->
      <div class="d-flex justify-content-between align-items-end">
        <ul class="nav nav-tabs mb-0 flex-grow-1">
          <li class="nav-item">
            <a class="nav-link" [class.active]="activeTab === 'all'" (click)="switchTab('all')" href="javascript:void(0)">All Bills</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" [class.active]="activeTab === 'by-manager'" (click)="switchTab('by-manager')" href="javascript:void(0)">By Manager</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" [class.active]="activeTab === 'rent-billing'" (click)="switchTab('rent-billing')" href="javascript:void(0)">Monthly Rent Billing</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" [class.active]="activeTab === 'by-property'" (click)="switchTab('by-property')" href="javascript:void(0)">By Property</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" [class.active]="activeTab === 'graphs'" (click)="switchTab('graphs')" href="javascript:void(0)">Graphs</a>
          </li>
        </ul>
        <div class="dropdown mb-1 ms-2" *ngIf="activeTab === 'all' && availableColumns.length"
            (click)="$event.stopPropagation()">
          <button class="btn btn-outline-secondary btn-sm dropdown-toggle" type="button"
              (click)="toggleColumnPicker()">
            <i class="bi bi-columns me-1"></i> Columns
          </button>
          <ul class="dropdown-menu dropdown-menu-end p-2 shadow" style="min-width: 220px;"
              [class.show]="showColumnPicker">
            <li *ngFor="let col of availableColumns" class="form-check">
              <input class="form-check-input" type="checkbox"
                  [id]="'acc-col-' + col.key"
                  [checked]="selectedColumnKeys.has(col.key)"
                  (change)="toggleColumn(col.key, $any($event.target).checked)">
              <label class="form-check-label" [for]="'acc-col-' + col.key">{{ col.label }}</label>
            </li>
          </ul>
        </div>
      </div>

      <!-- Bill Type Totals -->
      <div class="d-flex flex-wrap gap-3 py-2 px-1 border-bottom" *ngIf="billTypeTotalsList.length > 0">
        <span class="text-muted small fw-semibold me-1 align-self-center">Totals by Type:</span>
        <div *ngFor="let item of billTypeTotalsList" class="d-flex align-items-center gap-1">
          <span class="small fw-semibold" [ngClass]="getBillTypeTotalClass(item.label)">{{ item.label }}:</span>
          <span class="small">{{ formatAmount(item.amount) }}</span>
        </div>
      </div>

      <!-- Tab Content -->
      <div class="tab-content">
        <!-- All Bills Tab -->
        <div class="tab-pane" [class.active]="activeTab === 'all'">
          <div class="table-responsive">
            <table class="table table-hover table-sm mt-2">
              <thead>
                <tr>
                  <th *ngFor="let col of visibleColumns"
                      [class.text-end]="col.numeric"
                      [class.report-sortable]="col.sort"
                      (click)="sortColumn(col)">
                    {{ col.label }}
                    <i class="bi ms-1" *ngIf="col.sort" [ngClass]="sortIconClass(col)"></i>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let bill of allBillsData">
                  <td *ngFor="let col of visibleColumns" [class.text-end]="col.numeric">
                    <ng-container [ngSwitch]="col.key">
                      <span *ngSwitchCase="'bill_type'" class="badge" [ngClass]="getBillTypeClass(bill.bill_type)">{{ bill.bill_type }}</span>
                      <span *ngSwitchCase="'status'" class="badge" [ngClass]="getStatusClass(bill.status)">{{ bill.status }}</span>
                      <ng-container *ngSwitchCase="'amount'">{{ formatAmount(bill.amount) }}</ng-container>
                      <ng-container *ngSwitchCase="'total_amount'">{{ formatAmount(bill.total_amount) }}</ng-container>
                      <ng-container *ngSwitchCase="'creation_date'">{{ (bill.creation_date | date:'dd/MM/yyyy') || '-' }}</ng-container>
                      <ng-container *ngSwitchCase="'due_date'">{{ (bill.due_date | date:'dd/MM/yyyy') || '-' }}</ng-container>
                      <ng-container *ngSwitchCase="'paid_date'">{{ (bill.paid_date | date:'dd/MM/yyyy') || '-' }}</ng-container>
                      <ng-container *ngSwitchCase="'invoice'">
                        <span *ngIf="bill.invoice_generated" class="badge badge-info">Invoice</span>
                        <span *ngIf="!bill.invoice_generated" class="text-muted">-</span>
                      </ng-container>
                      <ng-container *ngSwitchDefault>{{ $any(bill)[col.key] || '-' }}</ng-container>
                    </ng-container>
                  </td>
                </tr>
                <tr *ngIf="allBillsData.length === 0">
                  <td [attr.colspan]="visibleColumns.length || 1" class="text-center text-muted py-3">No bills found</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="dataTables_paginate" *ngIf="pageCountAll > 1">
            <app-pagination [totalPage]="pageCountAll" [page]="pageAll" (newPage)="pageChangeAll($event)"></app-pagination>
          </div>
        </div>

        <!-- By Manager Tab -->
        <div class="tab-pane" [class.active]="activeTab === 'by-manager'">
          <div class="table-responsive">
            <table class="table table-hover table-sm mt-2">
              <thead>
                <tr>
                  <th>Manager</th>
                  <th class="text-end" [title]="totalPropertiesTooltip">{{ totalPropertiesHeader }}</th>
                  <th class="text-end">Total Bills</th>
                  <th class="text-end">Total Amount</th>
                  <th class="text-end">Paid Amount</th>
                  <th class="text-end">Pending Amount</th>
                  <th class="text-center">Paid</th>
                  <th class="text-center">Pending</th>
                  <th class="text-center">Overdue</th>
                  <th class="text-center">Cancelled</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of byManagerData">
                  <td>{{ row.manager_name }}</td>
                  <td class="text-end">{{ row.total_properties }}</td>
                  <td class="text-end">{{ row.total_bills }}</td>
                  <td class="text-end">{{ formatAmount(row.total_amount) }}</td>
                  <td class="text-end text-success">{{ formatAmount(row.paid_amount) }}</td>
                  <td class="text-end text-warning">{{ formatAmount(row.pending_amount) }}</td>
                  <td class="text-center"><span class="badge badge-success">{{ row.paid_count }}</span></td>
                  <td class="text-center"><span class="badge badge-warning">{{ row.pending_count }}</span></td>
                  <td class="text-center"><span class="badge badge-danger">{{ row.overdue_count }}</span></td>
                  <td class="text-center"><span class="badge badge-secondary">{{ row.cancelled_count }}</span></td>
                </tr>
                <tr *ngIf="byManagerData.length === 0">
                  <td colspan="10" class="text-center text-muted py-3">No data found</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="dataTables_paginate" *ngIf="pageCountByManager > 1">
            <app-pagination [totalPage]="pageCountByManager" [page]="pageByManager" (newPage)="pageChangeByManager($event)"></app-pagination>
          </div>
        </div>

        <!-- Monthly Rent Billing Tab -->
        <div class="tab-pane" [class.active]="activeTab === 'rent-billing'">
          <p class="text-muted small mb-1 mt-2">
            Month-wise Rent/Security Deposit billing for each manager's Rent Occupied properties.
            A property counts as billed for a month when a Rent/Security Deposit bill was created for it in that month;
            otherwise it is pending. Months follow the Start/End Date filter (by bill creation month, not paid date), up to the current month.
          </p>
          <div class="table-responsive">
            <table class="table table-hover table-sm mt-2">
              <thead>
                <tr>
                  <th>Property Manager</th>
                  <th>Month</th>
                  <th class="text-end" title="Rent Occupied properties assigned to the manager">Total Rent Occupied</th>
                  <th class="text-end" title="Properties with a Rent/Security Deposit bill created in the month">Billed</th>
                  <th class="text-end" title="Rent Occupied properties with no Rent/Security Deposit bill created in the month">Pending</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of rentBillingData">
                  <td>{{ row.manager_name }}</td>
                  <td>{{ row.month_label }}</td>
                  <td class="text-end">{{ row.total_properties }}</td>
                  <td class="text-end"><span class="badge badge-success">{{ row.billed_properties }}</span></td>
                  <td class="text-end">
                    <span class="badge" [ngClass]="row.pending_properties > 0 ? 'badge-warning' : 'badge-secondary'">{{ row.pending_properties }}</span>
                  </td>
                </tr>
                <tr *ngIf="rentBillingData.length === 0">
                  <td colspan="5" class="text-center text-muted py-3">No data found</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="dataTables_paginate" *ngIf="pageCountRentBilling > 1">
            <app-pagination [totalPage]="pageCountRentBilling" [page]="pageRentBilling" (newPage)="pageChangeRentBilling($event)"></app-pagination>
          </div>
        </div>

        <!-- By Property Tab -->
        <div class="tab-pane" [class.active]="activeTab === 'by-property'">
          <div class="table-responsive">
            <table class="table table-hover table-sm mt-2">
              <thead>
                <tr>
                  <th>Property</th>
                  <th class="text-end">Total Bills</th>
                  <th class="text-end">Total Amount</th>
                  <th class="text-end">Rent</th>
                  <th class="text-end">Expenses</th>
                  <th class="text-end">Company Fee</th>
                  <th class="text-end">Paid</th>
                  <th class="text-end">Pending</th>
                  <th class="text-center">Paid #</th>
                  <th class="text-center">Pending #</th>
                  <th class="text-center">Overdue #</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of byPropertyData">
                  <td>{{ row.property_name }}</td>
                  <td class="text-end">{{ row.total_bills }}</td>
                  <td class="text-end">{{ formatAmount(row.total_amount) }}</td>
                  <td class="text-end text-primary">{{ formatAmount(row.rent_amount) }}</td>
                  <td class="text-end text-warning">{{ formatAmount(row.expense_amount) }}</td>
                  <td class="text-end text-info">{{ formatAmount(row.company_fee_amount) }}</td>
                  <td class="text-end text-success">{{ formatAmount(row.paid_amount) }}</td>
                  <td class="text-end text-danger">{{ formatAmount(row.pending_amount) }}</td>
                  <td class="text-center"><span class="badge badge-success">{{ row.paid_count }}</span></td>
                  <td class="text-center"><span class="badge badge-warning">{{ row.pending_count }}</span></td>
                  <td class="text-center"><span class="badge badge-danger">{{ row.overdue_count }}</span></td>
                </tr>
                <tr *ngIf="byPropertyData.length === 0">
                  <td colspan="11" class="text-center text-muted py-3">No data found</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="dataTables_paginate" *ngIf="pageCountByProperty > 1">
            <app-pagination [totalPage]="pageCountByProperty" [page]="pageByProperty" (newPage)="pageChangeByProperty($event)"></app-pagination>
          </div>
        </div>

        <!-- Graphs Tab -->
        <div class="tab-pane" [class.active]="activeTab === 'graphs'">
          <div class="row mt-3" *ngIf="summaryData">
            <!-- Bills by Status Chart -->
            <div class="col-md-6 mb-3">
              <div class="card">
                <div class="card-header">
                  <h5 class="mb-0">Bills by Status</h5>
                </div>
                <div class="card-body chart-container">
                  <div class="chart-bars">
                    <div class="chart-bar-item" *ngFor="let key of getSummaryKeys(summaryData.count_by_status)">
                      <span class="chart-bar-label">{{ key }}</span>
                      <div class="chart-bar-wrapper">
                        <div class="chart-bar"
                          [style.width.%]="summaryData.total_bills > 0 ? (summaryData.count_by_status[key] / summaryData.total_bills) * 100 : 0"
                          [ngClass]="{
                            'bg-success': key === 'Paid',
                            'bg-warning': key === 'Pending',
                            'bg-danger': key === 'Overdue' || key === 'Unpaid',
                            'bg-secondary': key === 'Cancelled'
                          }">
                        </div>
                        <span class="chart-bar-value">{{ summaryData.count_by_status[key] }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Bills by Type Chart -->
            <div class="col-md-6 mb-3">
              <div class="card">
                <div class="card-header">
                  <h5 class="mb-0">Bills by Type</h5>
                </div>
                <div class="card-body chart-container">
                  <div class="chart-bars">
                    <div class="chart-bar-item" *ngFor="let key of getSummaryKeys(summaryData.count_by_bill_type)">
                      <span class="chart-bar-label">{{ key }}</span>
                      <div class="chart-bar-wrapper">
                        <div class="chart-bar"
                          [style.width.%]="summaryData.total_bills > 0 ? (summaryData.count_by_bill_type[key] / summaryData.total_bills) * 100 : 0"
                          [ngClass]="{
                            'bg-primary': key === 'Rent/Security Deposit',
                            'bg-warning': key === 'Expenses',
                            'bg-info': key === 'Company Fee',
                            'bg-secondary': key === 'Company Expense'
                          }">
                        </div>
                        <span class="chart-bar-value">{{ summaryData.count_by_bill_type[key] }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Amount by Type Chart -->
            <div class="col-md-6 mb-3" *ngIf="summaryData.amount_by_bill_type">
              <div class="card">
                <div class="card-header">
                  <h5 class="mb-0">Amount by Bill Type</h5>
                </div>
                <div class="card-body chart-container">
                  <div class="chart-bars">
                    <div class="chart-bar-item" *ngFor="let key of getSummaryKeys(summaryData.amount_by_bill_type)">
                      <span class="chart-bar-label">{{ key }}</span>
                      <div class="chart-bar-wrapper">
                        <div class="chart-bar"
                          [style.width.%]="summaryData.total_amount > 0 ? ((summaryData.amount_by_bill_type || {})[key] / summaryData.total_amount) * 100 : 0"
                          [ngClass]="{
                            'bg-primary': key === 'Rent/Security Deposit',
                            'bg-warning': key === 'Expenses',
                            'bg-info': key === 'Company Fee',
                            'bg-secondary': key === 'Company Expense'
                          }">
                        </div>
                        <span class="chart-bar-value">{{ formatAmount((summaryData.amount_by_bill_type || {})[key]) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Paid vs Pending Stacked Bar -->
            <div class="col-md-6 mb-3">
              <div class="card">
                <div class="card-header">
                  <h5 class="mb-0">Payment Overview</h5>
                </div>
                <div class="card-body chart-container">
                  <div class="chart-bars">
                    <div class="chart-bar-item stacked">
                      <span class="chart-bar-label">Amount</span>
                      <div class="chart-bar-stacked-wrapper">
                        <div class="stacked-bar">
                          <div class="stacked-segment bg-success"
                            [style.width.%]="summaryData.total_amount > 0 ? (summaryData.total_paid_amount / summaryData.total_amount) * 100 : 0">
                          </div>
                          <div class="stacked-segment bg-warning"
                            [style.width.%]="summaryData.total_amount > 0 ? (summaryData.total_pending_amount / summaryData.total_amount) * 100 : 0">
                          </div>
                        </div>
                        <span class="chart-bar-value">{{ formatAmount(summaryData.total_amount) }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="chart-legend mt-3">
                    <div class="legend-item">
                      <div class="legend-color bg-success"></div>
                      <span>Paid ({{ formatAmount(summaryData.total_paid_amount) }})</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color bg-warning"></div>
                      <span>Pending ({{ formatAmount(summaryData.total_pending_amount) }})</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Manager Performance Chart -->
            <div class="col-md-12 mb-3" *ngIf="byManagerData.length > 0">
              <div class="card">
                <div class="card-header">
                  <h5 class="mb-0">Manager Performance</h5>
                </div>
                <div class="card-body chart-container">
                  <div class="chart-bars">
                    <div class="chart-bar-item stacked" *ngFor="let manager of byManagerData">
                      <span class="chart-bar-label">{{ manager.manager_name }}</span>
                      <div class="chart-bar-stacked-wrapper">
                        <div class="stacked-bar">
                          <div class="stacked-segment bg-success"
                            [style.width.%]="manager.total_bills > 0 ? (manager.paid_count / manager.total_bills) * 100 : 0">
                          </div>
                          <div class="stacked-segment bg-warning"
                            [style.width.%]="manager.total_bills > 0 ? (manager.pending_count / manager.total_bills) * 100 : 0">
                          </div>
                          <div class="stacked-segment bg-danger"
                            [style.width.%]="manager.total_bills > 0 ? (manager.overdue_count / manager.total_bills) * 100 : 0">
                          </div>
                          <div class="stacked-segment bg-secondary"
                            [style.width.%]="manager.total_bills > 0 ? (manager.cancelled_count / manager.total_bills) * 100 : 0">
                          </div>
                        </div>
                        <span class="chart-bar-value">{{ manager.total_bills }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="chart-legend mt-3">
                    <div class="legend-item"><div class="legend-color bg-success"></div><span>Paid</span></div>
                    <div class="legend-item"><div class="legend-color bg-warning"></div><span>Pending</span></div>
                    <div class="legend-item"><div class="legend-color bg-danger"></div><span>Overdue</span></div>
                    <div class="legend-item"><div class="legend-color bg-secondary"></div><span>Cancelled</span></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Property Summary Chart -->
            <div class="col-md-12 mb-3" *ngIf="byPropertyData.length > 0">
              <div class="card">
                <div class="card-header">
                  <h5 class="mb-0">Property Revenue Breakdown</h5>
                </div>
                <div class="card-body chart-container">
                  <div class="chart-bars">
                    <div class="chart-bar-item stacked" *ngFor="let property of byPropertyData">
                      <span class="chart-bar-label">{{ property.property_name }}</span>
                      <div class="chart-bar-stacked-wrapper">
                        <div class="stacked-bar">
                          <ng-container *ngIf="property.total_amount > 0">
                            <div class="stacked-segment bg-primary"
                              [style.width.%]="(property.rent_amount / property.total_amount) * 100">
                            </div>
                            <div class="stacked-segment bg-warning"
                              [style.width.%]="(property.expense_amount / property.total_amount) * 100">
                            </div>
                            <div class="stacked-segment bg-info"
                              [style.width.%]="(property.company_fee_amount / property.total_amount) * 100">
                            </div>
                          </ng-container>
                        </div>
                        <span class="chart-bar-value">{{ formatAmount(property.total_amount) }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="chart-legend mt-3">
                    <div class="legend-item"><div class="legend-color bg-primary"></div><span>Rent</span></div>
                    <div class="legend-item"><div class="legend-color bg-warning"></div><span>Expenses</span></div>
                    <div class="legend-item"><div class="legend-color bg-info"></div><span>Company Fee</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div *ngIf="!summaryData" class="text-center text-muted py-4">
            No data available for charts
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Share Modal -->
<div class="modal d-block" tabindex="-1" role="dialog" *ngIf="showShareModal" style="background-color: rgba(0,0,0,0.5);">
  <div class="modal-dialog" role="document">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Share Report via {{ shareType === 'email' ? 'Email' : 'WhatsApp' }}</h5>
        <button type="button" class="btn-close" (click)="closeShareModal()"></button>
      </div>
      <form [formGroup]="shareForm">
        <div class="modal-body">
          <div class="mb-3">
            <label class="form-label">Search User</label>
            <ng-select
              formControlName="selectedUser"
              [items]="managersItems"
              bindLabel="label"
              bindValue="value"
              placeholder="Search by name..."
              (search)="searchManager$.next($event.term)"
              (change)="onShareUserChange($event)"
              class="select2-sm">
            </ng-select>
          </div>
          <div class="row mb-3">
            <div class="col-6">
              <label class="form-label">First Name</label>
              <input type="text" class="form-control form-control-sm" formControlName="first_name" placeholder="First name">
            </div>
            <div class="col-6">
              <label class="form-label">Last Name</label>
              <input type="text" class="form-control form-control-sm" formControlName="last_name" placeholder="Last name">
            </div>
          </div>
          <div class="mb-3" *ngIf="shareType === 'email'">
            <label class="form-label">Email Address</label>
            <input type="email" class="form-control form-control-sm" formControlName="email" placeholder="Enter email">
          </div>
          <div class="mb-3" *ngIf="shareType === 'whatsapp'">
            <div class="row">
              <div class="col-4">
                <label class="form-label">Code</label>
                <input type="text" class="form-control form-control-sm" formControlName="country_code" placeholder="+91">
              </div>
              <div class="col-8">
                <label class="form-label">WhatsApp Number</label>
                <input type="text" class="form-control form-control-sm" formControlName="whatsapp" placeholder="Enter number">
              </div>
            </div>
          </div>
          <div class="mb-3">
            <label class="form-label">File Type</label>
            <ng-select
              formControlName="file_type"
              [items]="reportTypeOptions"
              bindLabel="label"
              bindValue="value"
              [clearable]="false"
              class="select2-sm">
            </ng-select>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary btn-sm" (click)="closeShareModal()">Cancel</button>
          <button type="button" class="btn btn-primary btn-sm" (click)="submitShare()">
            <i class="bi" [ngClass]="{'bi-envelope': shareType === 'email', 'bi-whatsapp': shareType === 'whatsapp'}"></i>
            Send
          </button>
        </div>
      </form>
    </div>
  </div>
</div>
</div>
